import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface LookupOption { id: string; name: string }

function useLookup(name: string) {
  return useQuery({
    queryKey: ['lookup', name],
    queryFn: () => api<LookupOption[]>(`/api/lookups/${name}`),
    staleTime: 5 * 60_000,
  });
}

export const useClients = () => useLookup('clients');
export const useVendors = () => useLookup('vendors');
export const useMachines = () => useLookup('machines');
/** A machine with the name of its department (null when it has none). */
export interface MachineOption extends LookupOption { department?: string | null }
/** The printing machines: what a product's or a job card's machine is picked from. */
export const usePrintingMachines = () => useLookup('machines?department=Printing');
/** Every machine, removed ones too — to show the name of one an old record still carries. */
export const useAllMachines = () => useLookup('machines?all=1');

/**
 * Options for a machine box: the printing machines, plus the machine the
 * record already has when it is not one of them (an old or removed entry),
 * shown by name and marked, so it is not lost from view.
 */
export function machineOptions(printing: LookupOption[] | undefined, all: LookupOption[] | undefined, ...current: string[]): LookupOption[] {
  const list = [...(printing ?? [])];
  for (const id of current) {
    if (!id || list.some((o) => o.id === id)) continue;
    const old = (all ?? []).find((o) => o.id === id);
    if (old) list.push({ id, name: `${old.name} (old — pick a printing machine)` });
  }
  return list;
}
export const usePaperTypes = () => useLookup('paper-types');
export const useTopFinishes = () => useLookup('top-finishes');
export const useSheetsLookup = () => useLookup('sheets');
export const useDepartments = () => useLookup('departments');
export const useDesignations = () => useLookup('designations');
export const useCrms = () => useLookup('crms');
